import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import api from '../utils/api';
import { toast } from 'sonner';
import { ArrowLeft, RefreshCw, CheckCircle2, XCircle, Activity, Edit2, Settings, Monitor, MonitorPlay, ChevronDown, Download, Copy, Loader2 } from 'lucide-react';
import OverviewTab from '../components/asset-tabs/OverviewTab';
import AppsTab from '../components/asset-tabs/AppsTab';
import UsersTab from '../components/asset-tabs/UsersTab';
import RemoteTerminalTab from '../components/asset-tabs/RemoteTerminalTab';
import InfoTab from '../components/asset-tabs/InfoTab';
import ToolsTab from '../components/asset-tabs/ToolsTab';
import SettingsTab from '../components/asset-tabs/SettingsTab';
import ActivityTab from '../components/asset-tabs/ActivityTab';

const AssetDetailTabs = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [agent, setAgent] = useState(null);
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editSessionId, setEditSessionId] = useState('');
  const [saving, setSaving] = useState(false);
  const [showFriendlyNameDialog, setShowFriendlyNameDialog] = useState(false);
  const [editFriendlyName, setEditFriendlyName] = useState('');
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [showScreenshotModal, setShowScreenshotModal] = useState(false);
  const [screenshotTimestamp, setScreenshotTimestamp] = useState(Date.now());
  const [showRemoteControlDialog, setShowRemoteControlDialog] = useState(false);
  const [remoteControlProtocol, setRemoteControlProtocol] = useState('vnc');
  const [remoteCredentials, setRemoteCredentials] = useState({ username: '', password: '' });
  const [startingRemoteControl, setStartingRemoteControl] = useState(false);
  const [showRedeployDialog, setShowRedeployDialog] = useState(false);
  const [deploymentScript, setDeploymentScript] = useState(null);
  const [generatingScript, setGeneratingScript] = useState(false);
  
  // RustDesk state
  const [showRustDeskDialog, setShowRustDeskDialog] = useState(false);
  const [rustDeskId, setRustDeskId] = useState(null);
  const [rustDeskPassword, setRustDeskPassword] = useState(null);
  const [rustDeskExpiry, setRustDeskExpiry] = useState(null);
  const [requestingPassword, setRequestingPassword] = useState(false);
  const [passwordCountdown, setPasswordCountdown] = useState(null);

  useEffect(() => {
    fetchData();
  }, [id]);

  // Auto-copy credentials when RustDesk dialog opens
  useEffect(() => {
    if (showRustDeskDialog && rustDeskPassword) {
      // Automatically copy password to clipboard for quick paste
      navigator.clipboard.writeText(rustDeskPassword).then(() => {
        toast.success('Password copied to clipboard!', {
          description: 'Ready to paste when RustDesk prompts for password'
        });
      }).catch(() => {
        // Silent fail - user can still copy manually
      });
    }
  }, [showRustDeskDialog, rustDeskPassword]);

  const fetchData = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    
    try {
      const [agentRes, metricsRes] = await Promise.all([
        api.get(`/agents/${id}`),
        api.get(`/agents/${id}/metrics?hours=1`)
      ]);
      setAgent(agentRes.data);
      setMetrics(metricsRes.data.metrics || []);
      if (isRefresh) toast.success('Data refreshed!');
    } catch (error) {
      toast.error('Failed to load agent details');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleManualRefresh = () => {
    fetchData(true);
  };

  const handleEditSessionId = () => {
    setEditSessionId(agent.screenconnect_session_id || '');
    setShowEditDialog(true);
  };

  const handleSaveSessionId = async () => {
    setSaving(true);
    try {
      await api.put(`/agents/${agent.id}`, {
        screenconnect_session_id: editSessionId.trim() || null
      });
      
      // Update local state
      setAgent({...agent, screenconnect_session_id: editSessionId.trim() || null});
      
      toast.success('ScreenConnect session ID updated!');
      setShowEditDialog(false);
    } catch (error) {
      toast.error('Failed to update session ID');
    } finally {
      setSaving(false);
    }
  };

  const handleEditFriendlyName = () => {
    setEditFriendlyName(agent.friendly_name || '');
    setShowFriendlyNameDialog(true);
  };

  const handleSaveFriendlyName = async () => {
    setSaving(true);
    try {
      await api.put(`/agents/${agent.id}`, {
        friendly_name: editFriendlyName.trim() || null
      });
      
      // Update local state
      setAgent({...agent, friendly_name: editFriendlyName.trim() || null});
      
      toast.success('Friendly name updated!');
      setShowFriendlyNameDialog(false);
    } catch (error) {
      toast.error('Failed to update friendly name');
    } finally {
      setSaving(false);
    }
  };

  const getStatusIcon = (status) => {
    if (status === 'online') return <CheckCircle2 className="w-5 h-5 text-green-600" />;
    if (status === 'offline') return <XCircle className="w-5 h-5 text-red-600" />;
    return <Activity className="w-5 h-5 text-yellow-600" />;
  };

  const handleRemoteControl = async () => {
    try {
      const settingsRes = await api.get('/settings');
      const screenconnectUrl = settingsRes.data.screenconnect_url;
      
      if (!screenconnectUrl) {
        toast.error('ScreenConnect URL not configured. Please check Settings.');
        return;
      }
      
      // Clean URL (remove trailing slash)
      const baseUrl = screenconnectUrl.replace(/\/$/, '');
      const sessionId = agent.screenconnect_session_id;
      
      // Build ScreenConnect URL
      // Format: https://cmd-bitzukltd.screenconnect.com/Host#Access/All%20Machines%1FBitzUK//SESSION-GUID/Join
      const url = `${baseUrl}/Host#Access/All%20Machines%1FBitzUK//${sessionId}/Join`;
      
      // Open in centered popup window instead of tab
      const width = 1024;
      const height = 768;
      const left = (window.screen.width - width) / 2;
      const top = (window.screen.height - height) / 2;
      
      const popup = window.open(
        url,
        `ScreenConnect_${agent.hostname}`,
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,location=no,status=yes,resizable=yes,scrollbars=yes`
      );
      
      if (popup) {
        popup.focus();
        toast.success(`Opening remote session for ${agent.hostname}...`);
      } else {
        toast.error('Popup blocked! Please allow popups for this site.');
      }
    } catch (error) {
      toast.error('Failed to load ScreenConnect settings');
    }
  };

  const handleStartRemoteControl = async () => {
    if (!agent) return;

    setStartingRemoteControl(true);
    try {
      // Start remote control session
      const response = await api.post(`/agents/${agent.id}/remote-control/start`, {
        agent_id: agent.id,
        protocol: remoteControlProtocol,
        credentials: remoteCredentials.username ? remoteCredentials : null
      });

      if (response.data && response.data.session_id) {
        toast.success('Remote control session started!');
        
        // Open connection in new window/tab
        const width = 1280;
        const height = 800;
        const left = (window.screen.width - width) / 2;
        const top = (window.screen.height - height) / 2;
        
        // TODO: Once guacd is ready, this will point to Guacamole client
        // For now, we'll just show the connection details
        const connectionUrl = `/remote-control/${response.data.session_id}`;
        
        const remoteWindow = window.open(
          connectionUrl,
          `RemoteControl_${agent.hostname}`,
          `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,location=no,status=yes,resizable=yes,scrollbars=yes`
        );

        if (remoteWindow) {
          remoteWindow.focus();
          setShowRemoteControlDialog(false);
          setRemoteCredentials({ username: '', password: '' });
        } else {
          toast.error('Popup blocked! Please allow popups for this site.');
        }
      }
    } catch (error) {
      console.error('Failed to start remote control:', error);
      toast.error(error.response?.data?.detail || 'Failed to start remote control session');
    } finally {
      setStartingRemoteControl(false);
    }
  };

  // RustDesk Connection Handler
  const handleConnectRustDesk = async () => {
    if (!agent) return;
    
    setRequestingPassword(true);
    try {
      // Request one-time password
      const response = await api.post(`/agents/${agent.id}/rustdesk/request-password`);
      
      setRustDeskId(response.data.rustdesk_id);
      setRustDeskPassword(response.data.password);
      setRustDeskExpiry(new Date(response.data.expires_at));
      setShowRustDeskDialog(true);
      
      // Start countdown timer
      startPasswordCountdown(response.data.expires_in_seconds);
      
      toast.success('One-time password generated!');
    } catch (error) {
      console.error('Failed to generate RustDesk password:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate password');
    } finally {
      setRequestingPassword(false);
    }
  };
  
  const startPasswordCountdown = (seconds) => {
    setPasswordCountdown(seconds);
    
    const interval = setInterval(() => {
      setPasswordCountdown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    // Clean up on unmount
    return () => clearInterval(interval);
  };
  
  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard!`);
  };
  
  const openRustDesk = () => {
    if (!rustDeskId || !rustDeskPassword) {
      toast.error('Missing connection details');
      return;
    }

    // Method 1: Try RustDesk protocol handler with password
    // Format: rustdesk://connection/id=<ID>&password=<PASSWORD>
    const rustdeskUrl = `rustdesk://connection/new/${rustDeskId}?password=${encodeURIComponent(rustDeskPassword)}`;
    
    try {
      // Attempt to open RustDesk
      window.location.href = rustdeskUrl;
      toast.success('Launching RustDesk...', {
        description: 'If RustDesk doesn\'t open, use the manual connection option below'
      });
    } catch (error) {
      toast.error('Failed to launch RustDesk automatically. Please use manual connection.');
    }
  };

  const downloadRustDeskConfig = () => {
    if (!rustDeskId || !rustDeskPassword) return;

    // Create a .rdp-style config file that users can double-click
    const config = `[RustDesk Connection]
ID=${rustDeskId}
Password=${rustDeskPassword}
Hostname=${agent?.hostname || 'Remote Computer'}
GeneratedAt=${new Date().toISOString()}

Instructions:
1. Open RustDesk application
2. Enter ID: ${rustDeskId}
3. Enter Password: ${rustDeskPassword}
4. Click Connect

Security Notice:
This password expires in 5 minutes or after first use.
`;

    const blob = new Blob([config], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rustdesk-${agent?.hostname || 'connection'}-${Date.now()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success('Connection file downloaded!');
  };

  const launchRustDeskCLI = () => {
    if (!rustDeskId || !rustDeskPassword) return;

    // Generate a command that users can run
    const command = `rustdesk --connect ${rustDeskId} --password ${rustDeskPassword}`;
    
    navigator.clipboard.writeText(command).then(() => {
      toast.success('Command copied to clipboard!', {
        description: 'Run this command in your terminal to connect'
      });
    }).catch(() => {
      toast.error('Failed to copy command');
    });
  };


  const handleGenerateDeploymentScript = async (scriptType = 'powershell') => {
    setGeneratingScript(true);
    try {
      const response = await api.get(`/agents/${id}/deployment-script`, {
        params: { script_type: scriptType }
      });
      
      setDeploymentScript(response.data);
      toast.success('Deployment script generated!');
    } catch (error) {
      console.error('Failed to generate deployment script:', error);
      toast.error('Failed to generate deployment script');
    } finally {
      setGeneratingScript(false);
    }
  };

  const handleDownloadScript = () => {
    if (!deploymentScript) return;
    
    const blob = new Blob([deploymentScript.script], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = deploymentScript.filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    
    toast.success(`Downloaded ${deploymentScript.filename}`);
  };

  const handleCopyScript = () => {
    if (!deploymentScript) return;
    
    navigator.clipboard.writeText(deploymentScript.script);
    toast.success('Script copied to clipboard!');
  };

  if (loading) {

    return (
      <div className="flex items-center justify-center min-h-screen">
        <RefreshCw className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (!agent) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-lg text-gray-600">Agent not found</p>
          <Button onClick={() => navigate('/assets-rmm')} className="mt-4">
            Back to Assets
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-4 lg:p-6 space-y-6 max-w-full">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={() => navigate('/assets')}
              className="gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
            
            {/* Screenshot Thumbnail */}
            <div className="flex flex-col items-center gap-1">
              <div 
                className="relative cursor-pointer group"
                onClick={() => setShowScreenshotModal(true)}
              >
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}/api/agent/${id}/screenshot?t=${screenshotTimestamp}`}
                  alt="Live screenshot"
                  className="w-32 h-20 md:w-40 md:h-24 object-cover rounded-lg border-2 border-blue-400 shadow-md group-hover:border-blue-600 group-hover:shadow-lg transition-all"
                  title="Click to view fullsize"
                  onError={(e) => {
                    console.error('Screenshot failed to load');
                    e.target.src = 'data:image/svg+xml,%3Csvg xmlns=\"http://www.w3.org/2000/svg\" width=\"200\" height=\"150\" viewBox=\"0 0 200 150\"%3E%3Crect fill=\"%23f1f5f9\" width=\"200\" height=\"150\"/%3E%3Ctext x=\"50%25\" y=\"50%25\" dominant-baseline=\"middle\" text-anchor=\"middle\" font-family=\"sans-serif\" font-size=\"12\" fill=\"%2394a3b8\"%3ENo screenshot%3C/text%3E%3C/svg%3E';
                  }}
                />
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 rounded-lg flex items-center justify-center transition-all">
                  <Monitor className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
                </div>
                <div className="absolute -top-1 -right-1 bg-green-500 text-white text-xs px-1.5 py-0.5 rounded shadow-sm">
                  Live
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setScreenshotTimestamp(Date.now())}
                className="h-6 text-xs gap-1 text-slate-600 hover:text-blue-600"
                title="Refresh screenshot"
              >
                <RefreshCw className="w-3 h-3" />
                Refresh
              </Button>
            </div>
            
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">{agent.hostname}</h1>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleEditFriendlyName}
                  className="text-slate-400 hover:text-slate-600"
                  title="Edit friendly name"
                >
                  <Edit2 className="w-3 h-3" />
                </Button>
              </div>
              {agent.friendly_name && (
                <p className="text-sm text-slate-600 mt-1">{agent.friendly_name}</p>
              )}
              <div className="flex items-center gap-2 mt-1">
                {getStatusIcon(agent.status)}
                <span className="text-sm capitalize text-gray-600">{agent.status}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Remote Control Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="default"
                  className="gap-2 bg-blue-600 hover:bg-blue-700"
                  disabled={agent?.status !== 'online'}
                >
                  <MonitorPlay className="w-4 h-4" />
                  Remote Control
                  <ChevronDown className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem
                  onClick={handleConnectRustDesk}
                  disabled={agent?.status !== 'online' || requestingPassword || !agent?.rustdesk_id}
                  className="cursor-pointer"
                >
                  <Monitor className="w-4 h-4 mr-2 text-green-600" />
                  <div className="flex flex-col">
                    <span className="font-medium text-green-700">RustDesk (Recommended)</span>
                    <span className="text-xs text-gray-500">
                      {agent?.rustdesk_id && agent?.rustdesk_installed 
                        ? 'Secure one-time password' 
                        : agent?.rustdesk_installed === false 
                        ? 'Auto-installing...' 
                        : 'Checking installation...'}
                    </span>
                  </div>
                </DropdownMenuItem>
                
                <DropdownMenuSeparator />
                
                <DropdownMenuItem
                  onClick={() => setShowRemoteControlDialog(true)}
                  disabled={agent?.status !== 'online'}
                  className="cursor-pointer"
                >
                  <MonitorPlay className="w-4 h-4 mr-2" />
                  <div className="flex flex-col">
                    <span className="font-medium">VNC/RDP (Legacy)</span>
                    <span className="text-xs text-gray-500">Via browser (experimental)</span>
                  </div>
                </DropdownMenuItem>
                
                <DropdownMenuSeparator />
                
                <DropdownMenuItem
                  onClick={handleRemoteControl}
                  disabled={!agent.screenconnect_session_id}
                  className="cursor-pointer"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <div className="flex flex-col">
                    <span className="font-medium">ScreenConnect</span>
                    <span className="text-xs text-gray-500">
                      {agent.screenconnect_session_id ? 'Launch session' : 'Not configured'}
                    </span>
                  </div>
                </DropdownMenuItem>
                
                <DropdownMenuItem
                  onClick={handleEditSessionId}
                  className="cursor-pointer"
                >
                  <Edit2 className="w-4 h-4 mr-2" />
                  <span className="text-sm">Edit ScreenConnect ID</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Button
              onClick={handleManualRefresh}
              disabled={refreshing}
              variant="outline"
              className="gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            
            <Button
              onClick={() => {
                setShowRedeployDialog(true);
                handleGenerateDeploymentScript('powershell');
              }}
              variant="outline"
              className="gap-2"
              title="Generate deployment script"
            >
              <Download className="w-4 h-4" />
              Redeploy Agent
            </Button>
            
            <Button
              onClick={() => setShowSettingsDialog(true)}
              variant="outline"
              size="icon"
              title="Asset Settings"
            >
              <Settings className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <Card>
          <CardContent className="p-3 sm:p-4 lg:p-6">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-3 sm:grid-cols-6 mb-4 sm:mb-6 gap-1">
                <TabsTrigger value="overview" className="text-xs sm:text-sm">Overview</TabsTrigger>
                <TabsTrigger value="info" className="text-xs sm:text-sm">Info</TabsTrigger>
                <TabsTrigger value="tools" className="text-xs sm:text-sm">Tools</TabsTrigger>
                <TabsTrigger value="apps" className="text-xs sm:text-sm">Apps</TabsTrigger>
                <TabsTrigger value="users" className="text-xs sm:text-sm">Users</TabsTrigger>
                <TabsTrigger value="activity" className="text-xs sm:text-sm">Activity</TabsTrigger>
              </TabsList>

              <TabsContent value="overview">
                <OverviewTab agent={agent} metrics={metrics} />
              </TabsContent>

              <TabsContent value="info">
                <InfoTab agentId={id} />
              </TabsContent>

              <TabsContent value="tools">
                <ToolsTab agentId={id} agent={agent} />
              </TabsContent>

              <TabsContent value="apps">
                <AppsTab agentId={id} />
              </TabsContent>

              <TabsContent value="users">
                <UsersTab agentId={id} />
              </TabsContent>

              <TabsContent value="activity">
                <ActivityTab agentId={id} agent={agent} />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Dialogs */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit ScreenConnect Session ID</DialogTitle>
              <DialogDescription>
                Update the ScreenConnect session ID for {agent.hostname}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="session-id">Session ID (GUID)</Label>
                <Input
                  id="session-id"
                  value={editSessionId}
                  onChange={(e) => setEditSessionId(e.target.value)}
                  placeholder="e.g., 12345678-1234-1234-1234-123456789abc"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveSessionId} disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={showFriendlyNameDialog} onOpenChange={setShowFriendlyNameDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Friendly Name</DialogTitle>
              <DialogDescription>
                Set a friendly name for {agent.hostname}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="friendly-name">Friendly Name</Label>
                <Input
                  id="friendly-name"
                  value={editFriendlyName}
                  onChange={(e) => setEditFriendlyName(e.target.value)}
                  placeholder="e.g., John's Laptop, Sales PC 1"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowFriendlyNameDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveFriendlyName} disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Settings Dialog */}
        <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Asset Settings</DialogTitle>
              <DialogDescription>
                Configure settings for {agent.hostname}
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <SettingsTab agentId={id} />
            </div>
            <DialogFooter>
              <Button onClick={() => setShowSettingsDialog(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Screenshot Modal */}
        <Dialog open={showScreenshotModal} onOpenChange={setShowScreenshotModal}>
          <DialogContent className="max-w-6xl w-full">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Monitor className="w-5 h-5" />
                Live Desktop Screenshot - {agent?.hostname}
              </DialogTitle>
            </DialogHeader>
            <div className="relative">
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}/api/agent/${id}/screenshot?t=${screenshotTimestamp}`}
                alt="Fullsize screenshot"
                className="w-full h-auto max-h-[70vh] object-contain rounded-lg border border-slate-200"
                onError={(e) => {
                  e.target.src = 'data:image/svg+xml,%3Csvg xmlns=\"http://www.w3.org/2000/svg\" width=\"800\" height=\"600\" viewBox=\"0 0 800 600\"%3E%3Crect fill=\"%23f1f5f9\" width=\"800\" height=\"600\"/%3E%3Ctext x=\"50%25\" y=\"50%25\" dominant-baseline=\"middle\" text-anchor=\"middle\" font-family=\"sans-serif\" font-size=\"24\" fill=\"%2394a3b8\"%3ENo screenshot available%3C/text%3E%3C/svg%3E';
                }}
              />
            </div>
            <DialogFooter className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span>Auto-captured every 5 seconds</span>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setScreenshotTimestamp(Date.now())}
                  className="gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  Refresh
                </Button>
                <Button onClick={() => setShowScreenshotModal(false)}>
                  Close
                </Button>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>


      {/* Redeploy Agent Dialog */}
      <Dialog open={showRedeployDialog} onOpenChange={setShowRedeployDialog}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="w-5 h-5 text-blue-600" />
              Redeploy Agent - {agent?.hostname}
            </DialogTitle>
            <DialogDescription>
              Generate a custom deployment script to redeploy or update the agent on this machine
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 overflow-y-auto flex-1 pr-2">
            {generatingScript ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="w-10 h-10 animate-spin text-blue-600 mb-3" />
                <span className="text-gray-600">Generating deployment script...</span>
              </div>
            ) : deploymentScript ? (
              <>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5" />
                    Deployment Script Ready
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm text-blue-700">
                    <div>
                      <span className="font-medium block mb-1">Filename:</span>
                      <span className="font-mono text-xs">{deploymentScript.filename}</span>
                    </div>
                    <div>
                      <span className="font-medium block mb-1">Target:</span>
                      <span className="truncate block">{deploymentScript.agent_hostname}</span>
                    </div>
                    <div>
                      <span className="font-medium block mb-1">Generated:</span>
                      <span className="text-xs">{new Date(deploymentScript.generated_at).toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <Label className="text-base font-semibold">Script Type</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <Button
                      variant={deploymentScript.filename.endsWith('.ps1') ? 'default' : 'outline'}
                      onClick={() => handleGenerateDeploymentScript('powershell')}
                      className="h-auto py-3"
                    >
                      <div className="text-left">
                        <div className="font-semibold">PowerShell</div>
                        <div className="text-xs opacity-80">.ps1 (Recommended)</div>
                      </div>
                    </Button>
                    <Button
                      variant={deploymentScript.filename.endsWith('.bat') ? 'default' : 'outline'}
                      onClick={() => handleGenerateDeploymentScript('batch')}
                      className="h-auto py-3"
                    >
                      <div className="text-left">
                        <div className="font-semibold">Batch</div>
                        <div className="text-xs opacity-80">.bat</div>
                      </div>
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-base font-semibold">Script Preview</Label>
                  <div className="bg-gray-900 text-gray-100 p-4 rounded-lg font-mono text-xs overflow-auto" style={{maxHeight: '300px'}}>
                    <pre className="whitespace-pre-wrap break-all">{deploymentScript.script}</pre>
                  </div>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h4 className="font-semibold text-yellow-900 mb-3 flex items-center gap-2">
                    <Activity className="w-5 h-5" />
                    How to Use
                  </h4>
                  <ol className="text-sm text-yellow-800 space-y-2 list-decimal list-inside">
                    <li>Download the script using the <strong>Download</strong> button below</li>
                    <li>Copy the script to the target machine (<code className="bg-yellow-100 px-1 rounded">{agent?.hostname}</code>)</li>
                    <li>Right-click the script file → <strong>Run as Administrator</strong></li>
                    <li>Wait for the agent to update and reconnect</li>
                    <li>Verify the agent status in the dashboard</li>
                  </ol>
                </div>
              </>
            ) : null}
          </div>

          <DialogFooter className="flex-row justify-end gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => setShowRedeployDialog(false)}
            >
              Close
            </Button>
            {deploymentScript && (
              <>
                <Button
                  variant="outline"
                  onClick={handleCopyScript}
                  className="gap-2"
                >
                  <Copy className="w-4 h-4" />
                  Copy
                </Button>
                <Button
                  onClick={handleDownloadScript}
                  className="gap-2"
                >
                  <Download className="w-4 h-4" />
                  Download
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remote Control Dialog */}
      <Dialog open={showRemoteControlDialog} onOpenChange={setShowRemoteControlDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MonitorPlay className="w-5 h-5 text-blue-600" />
              Start Remote Control Session
            </DialogTitle>
            <DialogDescription>
              Connect to {agent?.hostname || 'this agent'} via remote desktop
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Protocol Selection */}
            <div className="space-y-2">
              <Label>Protocol</Label>
              <select
                value={remoteControlProtocol}
                onChange={(e) => setRemoteControlProtocol(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="vnc">VNC (Universal, works through firewall)</option>
                <option value="rdp">RDP (Native Windows, better performance)</option>
              </select>
              <p className="text-xs text-gray-500">
                {remoteControlProtocol === 'vnc' 
                  ? 'VNC uses WebSocket tunnel - works through firewalls'
                  : 'RDP requires port 3389 to be accessible'}
              </p>
            </div>

            {/* Credentials */}
            <div className="space-y-2">
              <Label>Windows Username (optional)</Label>
              <Input
                type="text"
                placeholder="Administrator"
                value={remoteCredentials.username}
                onChange={(e) => setRemoteCredentials({...remoteCredentials, username: e.target.value})}
                disabled={startingRemoteControl}
              />
            </div>

            <div className="space-y-2">
              <Label>Windows Password (optional)</Label>
              <Input
                type="password"
                placeholder="••••••••"
                value={remoteCredentials.password}
                onChange={(e) => setRemoteCredentials({...remoteCredentials, password: e.target.value})}
                disabled={startingRemoteControl}
              />
              <p className="text-xs text-gray-500">
                Credentials will be used for automatic login. Leave empty to enter manually.
              </p>
            </div>

            {/* Status Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <Activity className="w-4 h-4 text-blue-600 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium">Connection will open in new window</p>
                  <p className="text-xs mt-1">Session will be logged for audit purposes</p>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowRemoteControlDialog(false);
                setRemoteCredentials({ username: '', password: '' });
              }}
              disabled={startingRemoteControl}
            >
              Cancel
            </Button>
            <Button
              onClick={handleStartRemoteControl}
              disabled={startingRemoteControl || agent?.status !== 'online'}
              className="gap-2"
            >
              {startingRemoteControl ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Connecting...
                </>
              ) : (
                <>
                  <MonitorPlay className="w-4 h-4" />
                  Connect
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* RustDesk Connection Dialog */}
      <Dialog open={showRustDeskDialog} onOpenChange={setShowRustDeskDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="pb-4">
            <div className="flex items-center gap-2">
              <Monitor className="w-6 h-6 text-green-600" />
              <DialogTitle>Connect via RustDesk</DialogTitle>
            </div>
            <DialogDescription>
              Connect to {agent?.hostname} using one of the methods below
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Connection Credentials */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* RustDesk ID */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700">RustDesk ID</Label>
                <div className="flex items-center gap-2">
                  <Input 
                    value={rustDeskId || '...'} 
                    readOnly 
                    className="font-mono text-base flex-1"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(rustDeskId, 'RustDesk ID')}
                    disabled={!rustDeskId}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* One-Time Password */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium text-gray-700">Password</Label>
                  {passwordCountdown !== null && passwordCountdown > 0 && (
                    <span className="text-xs text-orange-600 font-medium flex items-center gap-1">
                      <Activity className="w-3 h-3 animate-pulse" />
                      {Math.floor(passwordCountdown / 60)}:{String(passwordCountdown % 60).padStart(2, '0')}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Input 
                    value={rustDeskPassword || '...'} 
                    readOnly 
                    type="text"
                    className="font-mono text-base flex-1"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(rustDeskPassword, 'Password')}
                    disabled={!rustDeskPassword}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Connection Methods */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Button
                onClick={openRustDesk}
                disabled={!rustDeskId || !rustDeskPassword}
                className="h-auto py-3 bg-green-600 hover:bg-green-700"
              >
                <div className="flex flex-col items-center gap-1">
                  <Monitor className="w-5 h-5" />
                  <span className="font-medium">Launch RustDesk</span>
                  <span className="text-xs opacity-90">Auto-fill password</span>
                </div>
              </Button>

              <Button
                onClick={launchRustDeskCLI}
                disabled={!rustDeskId || !rustDeskPassword}
                variant="outline"
                className="h-auto py-3"
              >
                <div className="flex flex-col items-center gap-1">
                  <Copy className="w-5 h-5" />
                  <span className="font-medium">Copy CLI Command</span>
                  <span className="text-xs opacity-70">For terminal users</span>
                </div>
              </Button>

              <Button
                onClick={downloadRustDeskConfig}
                disabled={!rustDeskId || !rustDeskPassword}
                variant="outline"
                className="h-auto py-3"
              >
                <div className="flex flex-col items-center gap-1">
                  <Download className="w-5 h-5" />
                  <span className="font-medium">Download Info</span>
                  <span className="text-xs opacity-70">Save credentials</span>
                </div>
              </Button>

              <Button
                variant="outline"
                onClick={() => setShowRustDeskDialog(false)}
                className="h-auto py-3"
              >
                <div className="flex flex-col items-center gap-1">
                  <XCircle className="w-5 h-5" />
                  <span className="font-medium">Close</span>
                  <span className="text-xs opacity-70">Cancel connection</span>
                </div>
              </Button>
            </div>

            {/* Info Boxes */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <Activity className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-blue-800">
                    <p className="font-medium mb-1">Quick Connect</p>
                    <p>Click "Launch RustDesk" for instant connection with pre-filled password</p>
                  </div>
                </div>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-green-800">
                    <p className="font-medium mb-1">Secure Access</p>
                    <p>One-time password expires in 5 minutes or after first use</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>


    </div>
  );
};

export default AssetDetailTabs;
